import { Pencil, Trash2 } from "lucide-react";
import { type FormEvent, useState } from "react";
import { CategoryIcon } from "@/components/CategoryIcon";
import { Button } from "@/components/ui/button";
import { Card, PageHeader } from "@/components/ui/card";
import { ErrorBanner, Loading, Notice } from "@/components/ui/feedback";
import { Select, TextInput } from "@/components/ui/field";
import { useCategories, useCreateCategory, useDeleteCategory, useUpdateCategory } from "../../api/finance";
import type { Category, CategoryKind } from "../../api/types";
import { useCan, useWorkspace } from "../../layout/workspace-context";

export function CategoriesPage() {
  const ws = useWorkspace();
  const canEdit = useCan("catalog:write");
  const categories = useCategories(ws.id);
  const [notice, setNotice] = useState<string | null>(null);

  if (categories.isPending) return <Loading />;
  if (categories.isError) return <ErrorBanner error={categories.error} />;

  return (
    <>
      <PageHeader title="Categorias" />
      {notice && (
        <div className="mb-4">
          <Notice>{notice}</Notice>
        </div>
      )}
      <div className="mb-8 grid gap-4 md:grid-cols-2">
        {(["expense", "income"] as const).map((kind) => (
          <Card key={kind} title={kind === "expense" ? "Despesas" : "Receitas"}>
            <ul
              aria-label={kind === "expense" ? "Categorias de despesa" : "Categorias de receita"}
              className="m-0 list-none p-0"
            >
              {categories.data
                .filter((c) => c.kind === kind && !c.parentId)
                .map((root) => (
                  <li key={root.id} className="border-b border-border py-2 last:border-0">
                    <CategoryRow category={root} canEdit={canEdit} onNotice={setNotice} />
                    <ul className="m-0 ml-5 list-none border-l border-border p-0 pl-4">
                      {categories.data
                        .filter((c) => c.parentId === root.id)
                        .map((child) => (
                          <li key={child.id} className="py-1">
                            <CategoryRow category={child} canEdit={canEdit} onNotice={setNotice} small />
                          </li>
                        ))}
                    </ul>
                  </li>
                ))}
            </ul>
          </Card>
        ))}
      </div>
      {canEdit && <NewCategoryForm categories={categories.data} />}
    </>
  );
}

function CategoryRow({
  category,
  canEdit,
  onNotice,
  small = false,
}: {
  category: Category;
  canEdit: boolean;
  onNotice: (text: string) => void;
  small?: boolean;
}) {
  const ws = useWorkspace();
  const update = useUpdateCategory(ws.id);
  const remove = useDeleteCategory(ws.id);
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [name, setName] = useState(category.name);

  async function rename(e: FormEvent) {
    e.preventDefault();
    await update.mutateAsync({ id: category.id, name: name.trim() });
    setEditing(false);
  }

  async function doDelete() {
    const { outcome } = await remove.mutateAsync(category.id);
    onNotice(
      outcome === "archived"
        ? `${category.name} está em uso e foi arquivada.`
        : `${category.name} foi excluída.`,
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex min-h-11 items-center gap-3">
        <CategoryIcon category={category} className={small ? "size-8 [&_svg]:size-4" : undefined} />
        {editing ? (
          <form onSubmit={(e) => void rename(e).catch(() => undefined)} className="min-w-0 flex-1">
            <label className="sr-only" htmlFor={`cat-${category.id}`}>
              Novo nome de {category.name}
            </label>
            <input
              id={`cat-${category.id}`}
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="min-h-9 w-full rounded-chip border border-border bg-surface px-2 text-[15px] text-ink focus-visible:border-accent focus-visible:outline-2 focus-visible:outline-accent/30"
            />
          </form>
        ) : (
          <span className={small ? "min-w-0 flex-1 truncate text-sm" : "min-w-0 flex-1 truncate font-medium"}>
            {category.name}
          </span>
        )}
        {canEdit && !editing && !confirming && (
          <span className="-mr-2 flex shrink-0">
            <Button
              variant="ghost"
              size="icon"
              aria-label={`Renomear ${category.name}`}
              title="Renomear"
              onClick={() => setEditing(true)}
            >
              <Pencil aria-hidden="true" className="size-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              aria-label={`Excluir ${category.name}`}
              title="Excluir"
              onClick={() => setConfirming(true)}
            >
              <Trash2 aria-hidden="true" className="size-4" />
            </Button>
          </span>
        )}
      </div>
      {confirming && (
        <div className="flex flex-wrap items-center gap-2 rounded-control bg-surface-2 p-3 text-sm">
          <span className="flex-1">Excluir {category.name}? Se estiver em uso, ela será arquivada.</span>
          <Button variant="ghost" onClick={() => setConfirming(false)}>
            Cancelar
          </Button>
          <Button
            variant="danger"
            aria-label={`Confirmar exclusão de ${category.name}`}
            disabled={remove.isPending}
            onClick={() => void doDelete().catch(() => undefined)}
          >
            Confirmar exclusão
          </Button>
        </div>
      )}
      <ErrorBanner error={update.error ?? remove.error} />
    </div>
  );
}

function NewCategoryForm({ categories }: { categories: Category[] }) {
  const ws = useWorkspace();
  const create = useCreateCategory(ws.id);
  const [name, setName] = useState("");
  const [kind, setKind] = useState<CategoryKind>("expense");
  const [parentId, setParentId] = useState("");
  const roots = categories.filter((c) => !c.parentId);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const parent = roots.find((r) => r.id === parentId);
    await create.mutateAsync({
      name: name.trim(),
      kind: parent?.kind ?? kind,
      ...(parent ? { parentId: parent.id } : {}),
    });
    setName("");
    setParentId("");
  }

  return (
    <Card title="Nova categoria" className="max-w-xl">
      <form onSubmit={(e) => void submit(e).catch(() => undefined)} className="flex flex-col gap-4">
        <TextInput
          label="Nome da categoria"
          required
          maxLength={60}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <Select
          label="Tipo"
          value={kind}
          onChange={(e) => setKind(e.target.value as CategoryKind)}
          disabled={Boolean(parentId)}
        >
          <option value="expense">Despesa</option>
          <option value="income">Receita</option>
        </Select>
        <Select label="Dentro de" value={parentId} onChange={(e) => setParentId(e.target.value)}>
          <option value="">Nenhuma (categoria principal)</option>
          {roots.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
            </option>
          ))}
        </Select>
        <ErrorBanner error={create.error} />
        <Button type="submit" disabled={create.isPending}>
          Criar categoria
        </Button>
      </form>
    </Card>
  );
}
